import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { PATH_SECTION_TEMPLATE_PRINTER } from '@e-socna/printer-contract';

import {
  PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
  PATH_SECTION_TEMPLATES,
  PATH_SECTION_TEMPLATE_ADMIN,
  PATH_SECTION_TEMPLATE_ADMIN_EDIT,
  PATH_SECTION_TEMPLATE_DEFAULT,
  PATH_SECTION_TEMPLATE_EDITOR,
  PATH_SECTION_TEMPLATE_INFO,
  PATH_SECTION_TEMPLATE_INFO_EDIT,
  PATH_SECTION_TEMPLATE_PREVIEW,
  PATH_SECTION_TEMPLATE_SUMMARY,
  PATH_SECTION_TEMPLATE_VERSIONS,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { DocumentEditor } from '../../components/DocumentEditor';
import { Loading } from '../../components/Loading';
import { FormVariant } from '../../forms';
import { BackupMode } from '../../components/DocumentEditor/enums/backup-mode.enum';
import { useIdParam } from '../../utils/use-id-param.util';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { useFindOneSectionTemplateQuery } from './hooks/useFindOneSectionTemplateQuery.hook';
import { useGetters } from './hooks/useGetters.hook';
import { SectionTemplateInfoTabPanel } from './SectionTemplateInfo';
import { useSaveHandler } from './hooks/useSaveHandler.hook';
import { useSavePreviewHandler } from './hooks/useSavePreviewHandler.hook';
import { usePrintHandler } from './hooks/usePrintHandler.hook';
import { useSavePrintSettingsHandler } from './hooks/useSavePrintSettingsHandler.hook';
import type { SectionTemplatePage_OneSectionTemplate } from './hooks/useFindOneSectionTemplateQuery.hook';

interface Props {
  readonly isInPrintMode?: boolean;
}

export const SectionTemplatePage = ({
  isInPrintMode = false,
}: Readonly<Props>) => {
  const { setPage } = useNavigationContext();
  const [isLoading, setIsLoading] = useState(false);
  const sectionTemplateId = useIdParam();

  const { paths: editorModePaths } = useBuildPaths(
    {
      Info: PATH_SECTION_TEMPLATE_INFO,
      InfoEdit: PATH_SECTION_TEMPLATE_INFO_EDIT,
      Editor: PATH_SECTION_TEMPLATE_EDITOR,
      Printer: PATH_SECTION_TEMPLATE_PRINTER,
      Summary: PATH_SECTION_TEMPLATE_SUMMARY,
      Preview: PATH_SECTION_TEMPLATE_PREVIEW,
      Versions: PATH_SECTION_TEMPLATE_VERSIONS,
      Admin: PATH_SECTION_TEMPLATE_ADMIN,
      AdminEdit: PATH_SECTION_TEMPLATE_ADMIN_EDIT,
    },
    { id: sectionTemplateId },
  );

  const { paths } = useBuildPaths(
    { default: PATH_SECTION_TEMPLATE_DEFAULT },
    { id: sectionTemplateId },
  );

  const { data, isSuccess } = useFindOneSectionTemplateQuery(sectionTemplateId);

  useEffect(() => {
    setPage(Page.DocumentAndSectionTemplates);
  }, [setPage]);

  const { onSave } = useSaveHandler({
    sectionTemplateId,
    editorModePaths,
    setIsLoading,
  });

  const { onSavePreview } = useSavePreviewHandler({
    sectionTemplateId,
    setIsLoading,
  });

  const { onPrint } = usePrintHandler({
    sectionTemplateId,
    snackbarActionTarget: paths.default,
  });

  const { onSavePrintSettings } = useSavePrintSettingsHandler({
    sectionTemplateId,
  });

  const getters = useGetters();

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            'Fiche Modèle de Section',
            data?.findOneSectionTemplate.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs
        paths={[
          {
            to: PATH_DOCUMENT_AND_SECTION_TEMPLATES_DEFAULT,
            label: 'modèles',
            key: 'templates',
          },
          {
            to: PATH_SECTION_TEMPLATES,
            label: 'sections',
            key: 'sectionTemplates',
          },
          {
            to: paths.default,
            label: 'fiche',
            key: 'sectionTemplate',
          },
        ]}
      />
    </>
  );

  const titleContent = <PageTitle title="Fiche Modèle de Section" />;

  if (isSuccess) {
    const sectionTemplate =
      data.findOneSectionTemplate as SectionTemplatePage_OneSectionTemplate;

    return (
      <>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!isLoading && <Loading />}
        {!isInPrintMode && (
          <>
            {beforeTitleContent}
            <PageTitle
              code={sectionTemplate.code}
              title={sectionTemplate.label}
            />
          </>
        )}
        <DocumentEditor
          componentSettings={{
            InfoTabPanel: SectionTemplateInfoTabPanel,
            welcomeMessage:
              'Votre modèle de section est vide pour le moment. Commencez le travail !',
          }}
          formSettings={{
            formVariant: FormVariant.Modification,
            initialEditable: sectionTemplate,
            isLoading,
          }}
          getters={getters}
          handlers={{
            onSave,
            onSavePreview,
            onPrint,
            onSavePrintSettings,
          }}
          resolversSettings={{
            allowedReturnTypeIds: undefined,
            enableDirectGeneration: false,
            mustBeResolvableOnBack: false,
          }}
          routerSettings={{
            editorModePaths,
            isInPrintMode,
          }}
          saveSettings={{
            askMemo: {
              beforePrint: false,
              beforeSave: true,
            },
            backupMode: BackupMode.Manual,
          }}
        />
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
